// ═══════════════════════════════════════════════════════════════════
//  SITE-LOCAL TIME
//
//  The staff calendar is drawn in the site's time zone, not the browser's. A
//  staff member opening it from another zone — or simply after 5pm Pacific,
//  when UTC has already rolled over — must still see the same week, the same
//  9:00 row, and the same day boundaries as the office does.
//
//  Everything here is therefore either a plain calendar date ("2026-08-17",
//  no zone attached at all) or an explicit conversion between such a date and
//  a real instant in a named zone. Nothing uses the browser's local zone.
// ═══════════════════════════════════════════════════════════════════

// Every MSC site is in one zone, and the API says so too: a site carries a
// single `timeZone`, which the server reads off the Cal team (CalTeam.TimeZone
// → StaffScheduleSite.TimeZone) and hands back on each site of the schedule.
//
// This constant is the fallback for the two cases where that value isn't there
// to be read: before the schedule has loaded — the date-range query has to be
// built to fetch it in the first place — and when Cal has no zone on the team,
// since `timeZone` is nullable all the way down. Once a site is in hand the
// grid resolves times against that site's own zone, not this.
export const SITE_TIME_ZONE = "America/Los_Angeles"

/** A calendar date with no time zone attached, "YYYY-MM-DD". */
export type CalendarDate = string

/** Minutes past midnight, site-local. The grid's vertical axis is measured in these. */
export type MinuteOfDay = number

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const

export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const

export interface WeekDate {
  /** "Monday" */
  day: string
  /** "Aug 17" */
  date: string
  /** "Monday, Aug 17" */
  full: string
  /** "2026-08-17" — the key everything else joins on. */
  isoDate: CalendarDate
}

function pad(value: number): string {
  return value.toString().padStart(2, "0")
}

/** Splits "2026-08-17" into its parts. Throws on anything else rather than silently drifting a day. */
function parts(date: CalendarDate): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) throw new Error(`Not a calendar date: "${date}"`)
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

/**
 * The year, month (1–12) and day of a "YYYY-MM-DD" date, or null when it is not a real calendar date
 * ("2026-02-30", "2026-8-1", "monday"). For reading dates that arrive from outside, such as a URL.
 */
export function calendarDateParts(date: string): { year: number; month: number; day: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const value = parts(date)
  const check = new Date(Date.UTC(value.year, value.month - 1, value.day))
  return check.getUTCFullYear() === value.year && check.getUTCMonth() === value.month - 1 && check.getUTCDate() === value.day
    ? value
    : null
}

function toCalendarDate(year: number, month: number, day: number): CalendarDate {
  return `${year.toString().padStart(4, "0")}-${pad(month)}-${pad(day)}`
}

/**
 * Calendar arithmetic done in UTC on purpose. UTC has no daylight saving, so "add a day" is always
 * exactly one calendar day; doing the same sum on a browser-local Date lands on 23:00 the previous
 * evening twice a year.
 */
export function addDays(date: CalendarDate, days: number): CalendarDate {
  const { year, month, day } = parts(date)
  const shifted = new Date(Date.UTC(year, month - 1, day + days))
  return toCalendarDate(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate())
}

/** 0 = Monday … 6 = Sunday, matching how the grid orders its columns. */
export function weekdayIndexOf(date: CalendarDate): number {
  const { year, month, day } = parts(date)
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7
}

/** The Monday of the week containing `date`. */
export function mondayOf(date: CalendarDate): CalendarDate {
  return addDays(date, -weekdayIndexOf(date))
}

/** The seven days of the week containing `anchor`, Monday first. */
export function weekDatesFrom(anchor: CalendarDate): WeekDate[] {
  const monday = mondayOf(anchor)
  return WEEKDAYS.map((day, index) => {
    const isoDate = addDays(monday, index)
    const { month, day: dayOfMonth } = parts(isoDate)
    const label = `${MONTH_NAMES[month - 1]} ${dayOfMonth}`
    return { day, date: label, full: `${day}, ${label}`, isoDate }
  })
}

/** "2026-08-17" → "Monday, Aug 17". The day the grid is showing, spelled out. */
export function longDateLabel(date: CalendarDate): string {
  const { month, day } = parts(date)
  return `${WEEKDAYS[weekdayIndexOf(date)]}, ${MONTH_NAMES[month - 1]} ${day}`
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = zoneFormatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    zoneFormatters.set(timeZone, formatter)
  }
  return formatter
}

function zonedFields(instant: Date, timeZone: string): Record<string, number> {
  const fields: Record<string, number> = {}
  for (const part of formatterFor(timeZone).formatToParts(instant)) {
    if (part.type !== "literal") fields[part.type] = Number(part.value)
  }
  return fields
}

/**
 * How far ahead of UTC `timeZone` is at that instant, in milliseconds (negative for the Americas).
 * Derived by formatting the instant in the zone and reading the wall clock back as if it were UTC —
 * the gap between the two is the offset. There is no API that returns it directly.
 */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const f = zonedFields(new Date(instant), timeZone)
  const asUtc = Date.UTC(f.year!, f.month! - 1, f.day!, f.hour!, f.minute!, f.second!)
  // formatToParts has second resolution, so compare against a second-truncated instant.
  return asUtc - Math.floor(instant / 1000) * 1000
}

/**
 * The instant at which the clock in `timeZone` reads `date` at `minutes` past midnight.
 *
 * Two passes because the offset depends on the answer: the first guess uses the offset at the same
 * wall time read as UTC, and the second corrects it when that guess landed on the other side of a
 * daylight-saving change. Times inside a spring-forward gap resolve to the instant the clock jumps
 * to; the calendar never asks for one, since business hours are mid-morning.
 */
export function zonedTimeToInstant(date: CalendarDate, minutes: MinuteOfDay, timeZone: string): Date {
  const { year, month, day } = parts(date)
  const wallClockAsUtc = Date.UTC(year, month - 1, day) + (minutes * 60_000)

  const firstGuess = wallClockAsUtc - zoneOffsetMs(wallClockAsUtc, timeZone)
  const secondOffset = zoneOffsetMs(firstGuess, timeZone)
  return new Date(wallClockAsUtc - secondOffset)
}

/** Midnight at the start of `date`, site-local. */
export function zonedDayStart(date: CalendarDate, timeZone: string): Date {
  return zonedTimeToInstant(date, 0, timeZone)
}

/** The last millisecond of `date`, site-local. */
export function zonedDayEnd(date: CalendarDate, timeZone: string): Date {
  return new Date(zonedTimeToInstant(addDays(date, 1), 0, timeZone).getTime() - 1)
}

/** Where an instant falls on the site's wall clock: which calendar date, and how far into it. */
export function zonedDateAndMinutes(instant: Date | string, timeZone: string): { date: CalendarDate; minutes: MinuteOfDay } {
  const value = instant instanceof Date ? instant : new Date(instant)
  const f = zonedFields(value, timeZone)
  return {
    date: toCalendarDate(f.year!, f.month!, f.day!),
    minutes: (f.hour! * 60) + f.minute!,
  }
}

/** Today's date on the site's wall clock — not the browser's, and not UTC's. */
export function todayInZone(timeZone: string = SITE_TIME_ZONE, now: Date = new Date()): CalendarDate {
  return zonedDateAndMinutes(now, timeZone).date
}

/** "09:00:00" or "09:00" (the API's wall-clock times) as minutes past midnight. */
export function timeStringToMinutes(time: string): MinuteOfDay {
  const match = /^(\d{1,2}):(\d{2})/.exec(time)
  if (!match) throw new Error(`Not a wall-clock time: "${time}"`)
  return (Number(match[1]) * 60) + Number(match[2])
}

/** 540 → "9:00 AM". The grid's row labels and the slot times both use this. */
export function minutesToLabel(minutes: MinuteOfDay): string {
  const normalized = ((minutes % 1440) + 1440) % 1440
  const hour24 = Math.floor(normalized / 60)
  const minute = normalized % 60
  const suffix = hour24 < 12 ? "AM" : "PM"
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${pad(minute)} ${suffix}`
}

// ── Query windows ─────────────────────────────────────────────────
// The instants the API is asked for. Built from site-local day boundaries, so a staff member in
// another time zone — or simply working late, when UTC has already rolled into tomorrow — asks for
// the same window the office would.

export interface DateWindow {
  start: string
  end: string
}

/**
 * One site-local calendar day, as instants.
 *
 * Bracketed on the site's clock rather than the browser's: an admin opening the calendar from
 * another zone — or simply after 5pm Pacific, when UTC has already rolled into tomorrow — asks for
 * the same day the office would.
 */
export function dayWindow(date: CalendarDate, timeZone: string = SITE_TIME_ZONE): DateWindow {
  return {
    start: zonedDayStart(date, timeZone).toISOString(),
    end: zonedDayEnd(date, timeZone).toISOString(),
  }
}

/** The visible Monday–Sunday week, as instants. */
export function weekWindow(anchor: CalendarDate, timeZone: string = SITE_TIME_ZONE): DateWindow {
  const monday = mondayOf(anchor)
  return {
    start: zonedDayStart(monday, timeZone).toISOString(),
    end: zonedDayEnd(addDays(monday, 6), timeZone).toISOString(),
  }
}

/** A whole calendar month relative to the anchor's: 0 is the anchor's own month, 1 the next. */
export function monthWindow(anchor: CalendarDate, monthOffset: number, timeZone: string = SITE_TIME_ZONE): DateWindow {
  const { year, month } = parts(anchor)
  const first = new Date(Date.UTC(year, month - 1 + monthOffset, 1))
  const firstDate = toCalendarDate(first.getUTCFullYear(), first.getUTCMonth() + 1, 1)
  const next = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 1))
  const lastDate = toCalendarDate(next.getUTCFullYear(), next.getUTCMonth() + 1, 1)
  return {
    start: zonedDayStart(firstDate, timeZone).toISOString(),
    end: zonedDayEnd(addDays(lastDate, -1), timeZone).toISOString(),
  }
}
